/**
 * PaymentMethodOrderPreview — 策略组的下发预览（ADM-APP-S013 / ADM-PAY-S008）
 *
 * 一个策略组（全局组或某个 Region 覆盖组）的 `payment_methods` 数组顺序就是 C 端展示次序（A8 §7.8.2，
 * 位置即 sort_order），所以预览只画「位次 + 图标 + 名称」。嵌在「支付通道策略」每个组的行里：
 * 全局和各 Region 覆盖各画各的，数据来自面板已加载的策略与图标设置，写操作成功后随 query 失效重取同步刷新。
 *
 * 同步取值在 Stripe 后台未启用时仍占位次，但当前不下发 —— 标红并压淡，让运营一眼分清「排了但没出」。
 * 不在可选列表的存量成员（目录已退役 / 不适用本 App，A8 §7.8.8）同样占位次、不下发，单独标「已退役 / 不可选」。
 * 卡品牌用目录固定图标（§7.8.8 `icon_fixed`），没有图标设置行、后台也拿不到其 URL —— 画中性占位，不标「待配」。
 */

import { NOT_SELECTABLE_BADGE } from './payment-method-labels';

export interface PaymentMethodOrderItem {
  method: string;
  label: string;
  /** 已配图标的公开 URL；`null` = 未配置（预览画占位） */
  iconUrl: string | null;
  /** `#RRGGBB`；`null` = 未配置，按白色显示（与 C 端契约一致） */
  backgroundColor: string | null;
  /** 同步取值且 Stripe 后台未启用：占位次但当前不下发 */
  unavailable: boolean;
  /** 不在 §7.8.8 可选列表（目录已退役等）：占位次但不下发，下次整组替换前须移出 */
  notSelectable?: boolean;
  /** 卡品牌固定图标（§7.8.8 `icon_fixed`）：无需配置，预览画中性占位 */
  iconFixed?: boolean;
}

/** 图标瓦片：C 端渲染 `icon_url`，底色可选，预览白色由契约指定、与后台明暗主题无关。 */
export function PaymentMethodOrderIconTile({
  item,
  size = 32,
}: {
  item: PaymentMethodOrderItem;
  size?: number;
}) {
  return item.iconUrl ? (
    <img
      draggable={false}
      src={item.iconUrl}
      alt={`${item.label} 图标`}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        border: '1px solid var(--border)',
        borderRadius: 6,
        background: item.backgroundColor ?? '#FFFFFF',
      }}
    />
  ) : item.iconFixed ? (
    <span
      className="chip"
      style={{ width: size, height: size, justifyContent: 'center', padding: 0, fontSize: 10 }}
      title={`${item.label} · 系统固定图标`}
    >
      固定
    </span>
  ) : (
    <span
      className="chip chip-warn"
      style={{ width: size, height: size, justifyContent: 'center', padding: 0, fontSize: 10 }}
      title={`${item.label} 未配置图标`}
    >
      待配
    </span>
  );
}

/**
 * 一个策略组的成员预览：自左向右即 C 端展示次序。
 * `highlighted` 里的成员用强调色描边（切换收单方确认页标出本次替换进来的成员）。
 */
export function PaymentMethodOrderStrip({
  items,
  highlighted,
}: {
  items: PaymentMethodOrderItem[];
  highlighted?: ReadonlySet<string>;
}) {
  return (
    <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'stretch' }}>
      {items.map((item, index) => (
        <div
          key={item.method}
          className="vstack"
          title={
            item.notSelectable
              ? `${item.label} · ${NOT_SELECTABLE_BADGE}，当前不下发`
              : item.unavailable
                ? `${item.label} · Stripe 后台未启用，当前不下发`
                : item.label
          }
          style={{
            gap: 4,
            alignItems: 'center',
            minWidth: 68,
            padding: '6px 8px',
            border: `1px solid ${highlighted?.has(item.method) ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 8,
            background: highlighted?.has(item.method) ? 'var(--accent-soft)' : 'var(--bg-elev)',
            opacity: item.unavailable || item.notSelectable ? 0.6 : 1,
          }}
        >
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
            #{index + 1}
          </span>
          <PaymentMethodOrderIconTile item={item} />
          <span
            style={{
              maxWidth: 88,
              fontSize: 10.5,
              textAlign: 'center',
              color: 'var(--fg-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </span>
          {item.unavailable && (
            <span className="chip chip-neg" style={{ fontSize: 9.5 }}>
              未启用
            </span>
          )}
          {item.notSelectable && (
            <span className="chip chip-neg" style={{ fontSize: 9.5 }}>
              {NOT_SELECTABLE_BADGE}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
